import React from 'react';
import { Document, Page, Text, View, Image, Font, Link } from '@react-pdf/renderer';
import { getRiskLevelColor, getRiskLevelText, getCoverPhotoId, getMapImage } from './reportUtils';
import { styles } from './reportStyles';
import { ReportData, FileAttachment } from './reportTypes';

interface ReportDocumentProps {
    reportData: ReportData;
    attachments: { [key: string]: string };
    userData: { firstName: string; lastName: string } | null;
}

// Fontları burada kaydetmek, her belge için gereklidir
Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 }
    ]
});

// Metin parçalama fonksiyonu - uzun metinleri daha küçük parçalara böler
const splitLongText = (text: string, maxLength: number = 500): string[] => {
    if (text.length <= maxLength) {
        return [text];
    }
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const chunks: string[] = [];
    let currentChunk = '';
    
    for (const sentence of sentences) {
        const trimmedSentence = sentence.trim();
        if (currentChunk.length + trimmedSentence.length + 1 <= maxLength) {
            currentChunk += (currentChunk ? '. ' : '') + trimmedSentence;
        } else {
            if (currentChunk) {
                chunks.push(currentChunk + '.');
                currentChunk = trimmedSentence;
            } else {
                // Eğer tek cümle çok uzunsa, kelime kelime böl
                const words = trimmedSentence.split(' ');
                let wordChunk = '';
                for (const word of words) {
                    if (wordChunk.length + word.length + 1 <= maxLength) {
                        wordChunk += (wordChunk ? ' ' : '') + word;
                    } else {
                        if (wordChunk) {
                            chunks.push(wordChunk);
                            wordChunk = word;
                        } else {
                            chunks.push(word);
                        }
                    }
                }
                if (wordChunk) {
                    currentChunk = wordChunk;
                }
            }
        }
    }
    
    if (currentChunk) {
        chunks.push(currentChunk + (currentChunk.endsWith('.') ? '' : '.'));
    }
    
    return chunks;
};

const ReportDocument: React.FC<ReportDocumentProps> = ({ reportData, attachments, userData }) => {
    // Kategori ve soruları gruplama mantığı
    const evaluationAnswer = reportData.questionAnswers.find(qa => 
        qa.answer.questionCategoryName && 
        qa.answer.questionCategoryName.toUpperCase() === "SONUÇ VE DEĞERLENDİRME"
    );
    const groupedQuestions = reportData.questionAnswers
        .filter(qa => 
            qa.answer.questionCategoryName && 
            qa.answer.questionCategoryName.toUpperCase() !== "SONUÇ VE DEĞERLENDİRME"
        )
        .reduce((acc, qa, originalIndex) => {
            const category = qa.answer.questionCategoryName;
            if (category && !acc[category]) { acc[category] = []; }
            if (category) {
                acc[category].push({ ...qa.answer, originalIndex });
            }
            return acc;
        }, {} as { [key: string]: any[] }) || {};
    
    Object.keys(groupedQuestions).forEach(category => {
        groupedQuestions[category].sort((a, b) => a.originalIndex - b.originalIndex);
    });

    const groupedRiskAnswers = reportData.riskAnswers.reduce((acc, ra) => {
        const category = ra.categoryName;
        if (!acc[category]) { acc[category] = []; }
        acc[category].push(ra);
        return acc;
    }, {} as { [key: string]: any[] }) || {};

    const getReportTitle = () => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        return `${day}.${month}.${year}_${reportData.pinInfo.Name}_rapor`;
    };

    const coverPhotoId = getCoverPhotoId(reportData);
    const mapImageId = getMapImage(reportData);
    // Dynamic TOC items
    // Keep anchors ASCII-safe to avoid intermittent navigation issues in PDF viewers.
    const slugify = (value: string) => {
        const lower = value.normalize('NFC').toLocaleLowerCase('tr-TR');
        const trToAscii = lower
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/ı/g, 'i');
        return trToAscii
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
    };

    // Turkish-safe title casing for TOC and headings
    const toTitleCaseTr = (text: string) =>
        text
            .normalize('NFC')
            .toLocaleLowerCase('tr-TR')
            .split(' ')
            .filter(Boolean)
            .map((w) => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1))
            .join(' ');

    const toUpperCaseTr = (text: string) => text.normalize('NFC').toLocaleUpperCase('tr-TR');

    // Only include categories that will actually render (prevents TOC links pointing to nowhere).
    const categoryHasContent = (category: string) => {
        const filteredQuestionAnswers =
            groupedQuestions[category]?.filter(
                (answer) =>
                    (answer.findings && answer.findings.trim() !== '') ||
                    (answer.proposedSolutions && answer.proposedSolutions.trim() !== '')
            ) || [];
        const hasQuestionAnswers = filteredQuestionAnswers.length > 0;
        const hasRiskAnswers = (groupedRiskAnswers[category]?.length ?? 0) > 0;
        return hasQuestionAnswers || hasRiskAnswers;
    };

    const orderedCategories = Array.from(
        new Set([...Object.keys(groupedQuestions), ...Object.keys(groupedRiskAnswers)])
    )
        .filter(categoryHasContent)
        .sort((a, b) => a.localeCompare(b, 'tr-TR'));

    // TOC order must match actual document order: static -> categories -> (optional) evaluation at the end.
    const tocStatic = [
        { label: 'Rapor Detayları', anchor: 'section-rapor-detaylari' },
        { label: 'Genel Bakış', anchor: 'section-genel-bakis' },
        { label: 'Risk ve Tehdit Analizi', anchor: 'section-risk-ve-tehdit-analizi' }
    ];

    const tocCategories = orderedCategories.map((cat) => ({
        label: toTitleCaseTr(cat),
        anchor: `cat-${slugify(cat)}`
    }));

    const tocTail = evaluationAnswer ? [{ label: 'Sonuç ve Değerlendirme', anchor: 'section-sonuc-degerlendirme' }] : [];
    
    return (
        <Document title={getReportTitle()}>
            {/* Kapak Sayfası */}
            <Page size="A4" style={styles.coverPage}>

                <View style={styles.coverContent}>
                    {/* content goes here */}
                    <View style={styles.logoIso}>
                        <Image src="/tav_logo_white.png" style={{ width: 110, height: 70 }} />
                    </View>
                    
                    <View style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', }}>
                        
                        {coverPhotoId && attachments[coverPhotoId] && (
                            <View style={styles.coverPhotoContainer}>
                                <Image source={attachments[coverPhotoId]} style={styles.coverPhoto} />
                            </View>
                        )}
                        
                        <View style={styles.reportTitle}>
                            <Text>TESİS RİSK VE TEHDİT</Text>
                            <Text>DEĞERLENDİRME</Text>
                            <Text>RAPORU</Text>
                        </View>

                            <Text style={styles.date}>
                            {new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase()}
                        </Text>
                        <Text style={styles.pinName}>{reportData.pinInfo.Name}</Text>
                    </View>

                    <Image src="/background.jpg" style={styles.backgroundImage} />

                    <View style={styles.footer}>
                        <Text>TAV Özel Güvenlik Hizmetleri A.Ş.</Text>
                        <Text>Strateji Geliştirme ve Kalite Kontrol Koordinatörlüğü</Text>
                    </View>
                </View>
            </Page>

            {/* İçindekiler (Table of Contents) */}
            <Page size="A4" style={styles.page}>
                <View style={styles.headerBar} fixed>
                    <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56 }} />
                    <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34 }} /> 
                </View>
                <View style={styles.contentContainer}>
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>İÇİNDEKİLER</Text>
                        <View>
                            {tocStatic.map((item, idx) => (
                                <View key={`toc-static-${idx}`} style={styles.tocRow}>
                                    <Link src={`#${item.anchor}`}>
                                        {/* Static labels are already curated; don't mutate casing (prevents TR character loss) */}
                                        <Text style={styles.tocTitle}>{`${idx + 1}. ${item.label}`}</Text>
                                    </Link>
                                </View>
                            ))}
                            {tocCategories.map((item, idx) => (
                                <View key={`toc-cat-${idx}`} style={styles.tocRow}>
                                    <Link src={`#${item.anchor}`}>
                                        <Text style={styles.tocTitle}>{`${tocStatic.length + idx + 1}. ${item.label}`}</Text>
                                    </Link>
                                </View>
                            ))}
                            {tocTail.map((item, idx) => (
                                <View key={`toc-tail-${idx}`} style={styles.tocRow}>
                                    <Link src={`#${item.anchor}`}>
                                        <Text style={styles.tocTitle}>{`${tocStatic.length + tocCategories.length + idx + 1}. ${item.label}`}</Text>
                                    </Link>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>
                <View style={styles.footer} fixed>
                    <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                    <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                </View>
            </Page>

            <Page size="A4" style={styles.page}>
                <View style={styles.headerBar} fixed>
                    <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56, opacity: 0.7 }} />
                    <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34, opacity: 0.7 }} /> 
                </View>
                <View style={styles.contentContainer}>
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle} id="section-rapor-detaylari" minPresenceAhead={120}>RAPOR DETAYLARI</Text>
                        
                        <Text style={styles.subheading}>Ait Olduğu Dönem / Tarih</Text>
                        <Text style={styles.paragraph}>
                        {new Date().toLocaleDateString('tr-TR')}
                        </Text>

                        <Text style={styles.subheading}>Düzenleyen Kurum / Kişi</Text>
                        <Text style={styles.paragraph}>
                        TAV Özel Güvenlik Hizmetleri A.Ş. {userData ? `/ ${userData.firstName} ${userData.lastName}` : ''}
                        </Text>

                        <Text style={styles.subheading}>Risk ve Tehdit Analizi Yapılan Kurum</Text>
                        <Text style={styles.paragraph}>
                        {reportData.pinInfo.Name}
                        </Text>

                        <Text style={styles.subheading}>Risk ve Tehdit Değerlendirme Yaklaşımı</Text>
                        <Text style={styles.paragraph}>
                        Risk ve Tehdit Değerlendirme Raporu; Kabiliyet, Maksat, Zafiyet, Kalıntı Risk kavramları dikkate alınarak ve Uzaktan Merkeze Değerlendirme Yöntemi ile hazırlanmıştır.
                        </Text>

                        <Text style={styles.subheading}>Risk ve Tehdit Değerlendirme Raporunun Amacı</Text>
                        <Text style={styles.bulletText}>• Risk ve tehdit unsurlarını belirlemek,</Text>
                        <Text style={styles.bulletText}>• Bu unsurların değerlendirmesini yaparak risk ve tehdit seviyesini tespit etmek,</Text>
                        <Text style={styles.bulletText}>• Tespit edilmiş risk ve tehdit seviyesine istinaden alınması gereken etkin karşı tedbirleri belirlemektir.</Text>
                    </View>

                    {/* Section 2: Genel Bakış */}
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle} id="section-genel-bakis" minPresenceAhead={120}>GENEL BAKIŞ</Text>
                        <Text style={styles.paragraph}>
                        {reportData.pinInfo.reportDescription || 'Genel bakış bilgisi bulunmamaktadır.'}
                        </Text>

                        {/* Charts (from Tesis Detay) */}
                        {attachments.__chart_weekly_line__ && (
                            <View wrap={false}>
                                <Text style={[styles.subheading, { marginTop: 14, marginBottom: 8 }]} minPresenceAhead={260}>
                                    Haftalık olay sayısı
                                </Text>
                                <View style={styles.chartCard}>
                                    <Image source={attachments.__chart_weekly_line__} style={styles.chartImage} />
                                </View>
                            </View>
                        )}

                        {attachments.__chart_eventtype_pie__ && (
                            <View wrap={false}>
                                <Text style={[styles.subheading, { marginTop: 14, marginBottom: 8 }]} minPresenceAhead={260}>
                                    Olay tiplerine göre dağılım
                                </Text>
                                <View style={styles.chartCard}>
                                    <Image source={attachments.__chart_eventtype_pie__} style={styles.chartImage} />
                                </View>
                            </View>
                        )}
                    </View>
                </View>
                <View style={styles.footer} fixed>
                    <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                    <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                </View>
            </Page>
            
            
            <Page size="A4" style={styles.page}>
                <View style={styles.headerBar} fixed>
                    <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56, opacity: 0.7 }} />
                    <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34, opacity: 0.7 }} /> 
                </View>
                <View style={styles.contentContainer}>
                <View style={styles.section}>
                    <Text style={styles.sectionTitle} id="section-risk-ve-tehdit-analizi" minPresenceAhead={160}>RİSK ANALİZİ METODOLOJİSİ</Text>

                    <Text style={styles.paragraph}>Bu rapor, güvenlik risklerini oluşturan ana bileşenler arasındaki nedensel ilişki zincirini aşağıdaki formüller ile modellemektedir.</Text>

                    <View style={[styles.smallTable, { marginTop: 8 }]} wrap={false}>
                        <View style={styles.smallTableHeader}>
                            <Text style={styles.smallTableHeaderCell}>Formül</Text>
                            <Text style={styles.smallTableHeaderCell}>Açıklama</Text>
                            <Text style={styles.smallTableHeaderCell}>İlgili Fonksiyon</Text>
                        </View>
                        <View style={styles.smallTableRow}>
                            <Text style={styles.smallTableCell}>Kabiliyet + Maksat → Tehdit</Text>
                            <Text style={styles.smallTableCell}>Aktörün eylem kapasitesi ile niyetinin birleşimi</Text>
                            <Text style={styles.smallTableCell}>İstihbarat</Text>
                        </View>
                        <View style={styles.smallTableRow}>
                            <Text style={styles.smallTableCell}>Tehdit + Zafiyet → Risk</Text>
                            <Text style={styles.smallTableCell}>Tehditlerin mevcut güvenlik açıklarıyla etkileşimi</Text>
                            <Text style={styles.smallTableCell}>Operasyon & Araştırma</Text>
                        </View>
                        <View style={styles.smallTableRow}>
                            <Text style={styles.smallTableCell}>Risk – Etkin Karşı Tedbirler → Kalıntı Risk</Text>
                            <Text style={styles.smallTableCell}>Alınan önlemler sonrası geriye kalan risk seviyesi</Text>
                            <Text style={styles.smallTableCell}>Önleyici Güvenlik Planlaması</Text>
                        </View>
                    </View>

                    <View style={{ marginTop: 8 }} wrap={false}>
                        <View style={styles.definitionRow}>
                            <Text style={styles.definitionTerm}>Maksat</Text>
                            <Text style={styles.definitionColon}>:</Text>
                            <Text style={styles.definitionDesc}>Eylem gerçekleştirme niyeti.</Text>
                        </View>
                        <View style={styles.definitionRow}>
                            <Text style={styles.definitionTerm}>Kabiliyet</Text>
                            <Text style={styles.definitionColon}>:</Text>
                            <Text style={styles.definitionDesc}>Bu niyetin fiiliyata dönüştürülebilmesini kolaylaştıran imkân ve kapasite.</Text>
                        </View>
                        <View style={styles.definitionRow}>
                            <Text style={styles.definitionTerm}>Tehdit</Text>
                            <Text style={styles.definitionColon}>:</Text>
                            <Text style={styles.definitionDesc}>Maksat ve kabiliyetin birleşiminden doğan potansiyel tehlike.</Text>
                        </View>
                        <View style={styles.definitionRow}>
                            <Text style={styles.definitionTerm}>Risk</Text>
                            <Text style={styles.definitionColon}>:</Text>
                            <Text style={styles.definitionDesc}>Bu tehdidin, mevcut zafiyetlerle etkileşime girmesi sonucu oluşan zarar ihtimali.</Text>
                        </View>
                    </View>

                    <Text style={[styles.sectionTitle, { fontSize: 14, marginTop: 10 }]}>RİSK DEĞERLENDİRME SKALASI</Text>
                    <Text style={styles.paragraph}>Risk seviyesi, nitelikli saha gözlemleri, yapay zekâ destekli veri analizi ve uzman değerlendirmeleri doğrultusunda aşağıdaki ölçeklendirme kriterlerine göre sınıflandırılmıştır.</Text>

                    <Text style={[styles.paragraph, { marginTop: 6 }]}>
                        Yukarıdaki formüle göre belirlenen riskler için aşağıdaki değerleme yaklaşımı doğrultusunda etkin karşı tedbirler belirlenmiştir.
                    </Text>
                    <View style={[styles.smallTable, { marginTop: 5 }]} wrap={false}>
                        <View style={styles.smallTableHeader}>
                            <Text style={styles.smallTableHeaderCell}>Risk ve Tehdit Değer Aralığı</Text>
                            <Text style={styles.smallTableHeaderCell}>Risk Seviyesi</Text>
                        </View>
                        <View style={styles.smallTableRow}><Text style={styles.smallTableCell}>{'r ≤ 3'}</Text><Text style={[styles.smallTableCell, { backgroundColor: '#e0e0e0' }]}>Düşük</Text></View>
                        <View style={styles.smallTableRow}><Text style={styles.smallTableCell}>{'3 < r ≤ 6'}</Text><Text style={[styles.smallTableCell, { backgroundColor: '#fff176' }]}>Orta</Text></View>
                        <View style={styles.smallTableRow}><Text style={styles.smallTableCell}>{'6 < r < 12'}</Text><Text style={[styles.smallTableCell, { backgroundColor: '#ffb74d' }]}>Yüksek</Text></View>
                        <View style={styles.smallTableRow}><Text style={styles.smallTableCell}>{'r ≥ 12'}</Text><Text style={[styles.smallTableCell, { backgroundColor: '#ef5350', color: 'white' }]}>Kritik</Text></View>
                    </View>
                    <Text style={[styles.paragraph]}>
                        Güvenlik halkaları değerlendirme metoduna göre, dışarıdan içeriye doğru Dış, Orta ve İç güvenlik halkaları dikkate alınmıştır.
                    </Text>

                    <View wrap={false}>
                        <Text style={[styles.subheading, { marginTop: 15, marginBottom: 8 }]}>Uygulanan Yaklaşım</Text>
                        <Text style={styles.paragraph}>
                            Değerlendirme sürecinde ayrıca fiziksel güvenliğin katmanlı yapısını esas alan "Güvenlik Halkaları Yaklaşımı" benimsenmiştir. Analizler; Dış, Orta ve İç Güvenlik Halkası düzeylerinde gerçekleştirilmiştir.
                        </Text>
                    </View>

                    <Text style={[styles.sectionTitleNoBorder, { fontSize: 18, marginTop: 20, marginBottom: 0 }]}>TESİS RİSK VE TEHDİT DEĞERLENDİRME RAPORU</Text>
                    <Text style={[styles.sectionTitle, { fontSize: 18, marginTop: 0, marginBottom: 15 }]}>{reportData.pinInfo.Name}</Text>
                    {/* 
                    <Text style={[styles.subheading, { marginTop: 15, marginBottom: 8 }]}>Fiziksel Çevre ve Yapı Analizi</Text>
                    <View style={{ marginTop: 8, marginBottom: 15, textAlign: 'center' }}>
                        <Image src="/placeholder_report.png" style={styles.mapImage} />
                    </View>
                    */}

                    {/* Keep the heading + map image together; if it doesn't fit, start on next page */}
                    <View wrap={false}>
                        <Text style={[styles.subheading, { marginTop: 15, marginBottom: 8 }]} minPresenceAhead={220}>
                            Fonksiyonel Konum ve Erişim Ağı
                        </Text>
                        {mapImageId && attachments[mapImageId] && (
                            <View style={{ marginTop: 10, marginBottom: 20 }}>
                                <Image source={attachments[mapImageId]} style={styles.mapImage} />
                                <Text style={[styles.paragraph, { marginTop: 8 }]}>
                                    Tesis sayfasında seçilen yarıçap {reportData.pinInfo.RadiusInMeters} m.
                                </Text>
                            </View>
                        )}
                    </View>
                </View>
                </View>
                <View style={styles.footer} fixed>
                    <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                    <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                </View>
            </Page>

            {orderedCategories.map(category => {
                const filteredQuestionAnswers = groupedQuestions[category]?.filter(answer => (answer.findings && answer.findings.trim() !== '') || (answer.proposedSolutions && answer.proposedSolutions.trim() !== '')) || [];
                const hasQuestionAnswers = filteredQuestionAnswers.length > 0;
                const hasRiskAnswers = groupedRiskAnswers[category]?.length > 0;
                if (!hasQuestionAnswers && !hasRiskAnswers) return null;

                const categoryAnchor = `cat-${slugify(category)}`;

                return (
                    <React.Fragment key={category}>
                        {hasQuestionAnswers && (
                            <Page size="A4" style={styles.page}>
                                <View style={styles.headerBar} fixed>
                                    <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56,opacity: 0.7 }} />
                                    <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34,opacity: 0.7 }} /> 
                                </View>
                                <View style={styles.contentContainer}>
                                <View style={styles.section} wrap={true}>
                                    <Text style={styles.sectionTitle} id={categoryAnchor} minPresenceAhead={120}>
                                        {toUpperCaseTr(category)}
                                    </Text>
                                    <View style={styles.articleContainer} wrap={true}>
                                        {filteredQuestionAnswers.map((answer, answerIndex) => {
                                            const images = (answer.fileAttachments || []).filter((file: FileAttachment) => 
                                                !!attachments[file.id] && 
                                                typeof attachments[file.id] === 'string' && 
                                                attachments[file.id].startsWith('data:')
                                            );
                                            return (
                                                <View key={answerIndex} style={{ marginBottom: 12 }} wrap={true}>
                                                    {answer.findings && answer.findings.trim() !== '' && (
                                                        <View wrap={true}>
                                                            <Text style={[styles.articleParagraph, { fontWeight: 'bold' }]}>{answerIndex + 1}. Bulgu:</Text>
                                                            {splitLongText(answer.findings).map((chunk, chunkIndex) => (
                                                                <Text key={chunkIndex} style={styles.articleParagraph}>{chunk}</Text>
                                                            ))}
                                                        </View>
                                                    )}
                                                    {answer.proposedSolutions && answer.proposedSolutions.trim() !== '' && (
                                                        <View wrap={true}>
                                                            <Text style={[styles.articleParagraph, { fontWeight: 'bold' }]}>{answerIndex + 1}. Öneri:</Text>
                                                            {splitLongText(answer.proposedSolutions).map((chunk, chunkIndex) => (
                                                                <Text key={chunkIndex} style={styles.articleParagraph}>{chunk}</Text>
                                                            ))}
                                                        </View>
                                                    )}
                                                    {images.length > 0 && images.map((file: FileAttachment, imageIndex: number) => (
                                                        <View key={imageIndex} wrap={false} style={{ marginTop: imageIndex > 0 ? 10 : 0 }}>
                                                            <Image 
                                                                src={attachments[file.id]} 
                                                                style={styles.articleImageCentered}
                                                            />
                                                        </View>
                                                    ))}
                                                </View>
                                            );
                                        })}
                                    </View>
                                </View>
                                </View>
                                <View style={styles.footer} fixed>
                                    <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                                    <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                                </View>
                            </Page>
                        )}
                        {hasRiskAnswers && (
                            <Page size="A4" style={styles.page}>
                                <View style={styles.headerBar} fixed>
                                    <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56, opacity: 0.7 }} />
                                    <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34, opacity: 0.7}} /> 
                                </View>
                                <View style={styles.contentContainer}>
                                <View style={styles.section} wrap={true}>
                                    <Text
                                        style={styles.sectionTitle}
                                        // If there is no "question answers" page, the TOC should land here.
                                        id={!hasQuestionAnswers ? categoryAnchor : undefined}
                                        minPresenceAhead={120}
                                    >
                                        {toUpperCaseTr(category)} - RİSK TABLOSU
                                    </Text>
                                    <View style={{ marginTop: 20 }}>
                                        {(() => {
                                            const categoryRiskAnswers = groupedRiskAnswers[category];
                                            const beforeMeasures = categoryRiskAnswers.find(ra => !ra.isAfterMeasures);
                                            const afterMeasures = categoryRiskAnswers.find(ra => ra.isAfterMeasures);
                                            return (
                                                <>
                                                    {beforeMeasures && (
                                                        <View style={{ marginBottom: 20 }} wrap={false}>
                                                            <Text style={[styles.subheading, { marginBottom: 10, fontSize: 13, fontWeight: 'bold', color: '#094151' }]}>
                                                                Etkin Karşı Tedbirler Öncesi Risk ve Tehdit Seviyesi
                                                            </Text>
                                                            <View style={styles.table}>
                                                                <View style={styles.tableHeader}>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Olasılık Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Etki Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Tehdit Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Uygulanan Tedbirler Kat Sayısı</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Kalıntı Risk Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center', borderRight: 'none' }]}>Risk ve Tehdit Seviyesi</Text>
                                                                </View>
                                                                <View style={styles.tableRow}>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{beforeMeasures.probabilityLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{beforeMeasures.impactLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{beforeMeasures.threatLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{beforeMeasures.appliedMeasureCoefficient}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{beforeMeasures.residualRiskLevel}</Text>
                                                                    <Text style={[styles.tableCell, { 
                                                                        flex: 1, 
                                                                        backgroundColor: getRiskLevelColor(beforeMeasures.residualRiskLevel).backgroundColor, 
                                                                        color: getRiskLevelColor(beforeMeasures.residualRiskLevel).color, 
                                                                        fontWeight: 'bold', 
                                                                        textAlign: 'center',
                                                                        paddingVertical: 8,
                                                                        borderRadius: 4,
                                                                        borderRight: 'none'
                                                                    }]}>{getRiskLevelText(beforeMeasures.residualRiskLevel)}</Text>
                                                                </View>
                                                            </View>
                                                        </View>
                                                    )}
                                                    {afterMeasures && (
                                                        <View style={{ marginBottom: 20 }} wrap={false}>
                                                            <Text style={[styles.subheading, { marginBottom: 10, fontSize: 13, fontWeight: 'bold', color: '#094151' }]}>
                                                                Etkin Karşı Tedbirler Sonrası Risk ve Tehdit Seviyesi
                                                            </Text>
                                                            <View style={styles.table}>
                                                                <View style={styles.tableHeader}>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Olasılık Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Etki Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Tehdit Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Uygulanan Tedbirler Kat Sayısı</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>Kalıntı Risk Seviyesi</Text>
                                                                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center', borderRight: 'none' }]}>Risk ve Tehdit Seviyesi</Text>
                                                                </View>
                                                                <View style={styles.tableRow}>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{afterMeasures.probabilityLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{afterMeasures.impactLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{afterMeasures.threatLevel}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{afterMeasures.appliedMeasureCoefficient}</Text>
                                                                    <Text style={[styles.tableCell, { flex: 1, textAlign: 'center', fontWeight: 'bold' }]}>{afterMeasures.residualRiskLevel}</Text>
                                                                    <Text style={[styles.tableCell, { 
                                                                        flex: 1, 
                                                                        backgroundColor: getRiskLevelColor(afterMeasures.residualRiskLevel).backgroundColor, 
                                                                        color: getRiskLevelColor(afterMeasures.residualRiskLevel).color, 
                                                                        fontWeight: 'bold', 
                                                                        textAlign: 'center',
                                                                        paddingVertical: 8,
                                                                        borderRadius: 4,
                                                                        borderRight: 'none'
                                                                    }]}>{getRiskLevelText(afterMeasures.residualRiskLevel)}</Text>
                                                                </View>
                                                            </View>
                                                        </View>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </View>
                                </View>
                                </View>
                                <View style={styles.footer} fixed>
                                    <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                                    <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                                </View>
                            </Page>
                        )}
                    </React.Fragment>
                );
            })}
            {evaluationAnswer && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.headerBar} fixed>
                        <Image src="/tav_logo_pdf_header.png" style={{ width: 88, height: 56, opacity: 0.7 }} />
                        <Image src="/iso_logo_pdf_header.png" style={{ width: 95, height: 34, opacity: 0.7 }} /> 
                    </View>
                    <View style={styles.contentContainer}>
                    <View style={styles.section} wrap={true}>
                        {/* Always keep this section as the final part of the report */}
                        <Text style={styles.sectionTitle} id="section-sonuc-degerlendirme" minPresenceAhead={120}>
                            SONUÇ VE DEĞERLENDİRME
                        </Text>

                        {/* Same style as the rest of the report: plain paragraphs (no box, no special background) */}
                        <View wrap={true}>
                            {splitLongText(evaluationAnswer!.answer.description || 'Değerlendirme bilgisi bulunmamaktadır.').map((chunk, chunkIndex) => (
                                <Text key={chunkIndex} style={[styles.paragraph, { marginBottom: 6 }]}>
                                    {chunk}
                                </Text>
                            ))}
                        </View>

                        {evaluationAnswer!.answer.fileAttachments?.length > 0 && (
                            <View style={styles.attachmentContainer} wrap={true}>
                                {evaluationAnswer!.answer.fileAttachments
                                    .filter((file: FileAttachment) =>
                                        !!attachments[file.id] &&
                                        typeof attachments[file.id] === 'string' &&
                                        attachments[file.id].startsWith('data:')
                                    )
                                    .map((file: FileAttachment, index: number) => (
                                        <Image key={index} src={attachments[file.id]} style={styles.attachmentImage} />
                                    ))}
                            </View>
                        )}
                    </View>
                    </View>
                    <View style={styles.footer} fixed>
                        <Text style={styles.footerTextLeft}>TAV-GR-2025/07-01 | GF-6.4.3.</Text>
                        <Text style={styles.footerTextRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                    </View>
                </Page> 
            )}
        </Document>
    );
};

export default ReportDocument;